import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section className="relative h-[520px] w-full overflow-hidden bg-primary-950 md:h-[620px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照背景`}
            title={`${item.title} 影视推荐`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/55 to-primary-950/35"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full flex-col items-center justify-center px-4 text-center md:px-6">
        <span className="mb-4 flex items-center gap-2 rounded-full bg-accent-500/90 px-4 py-1 text-xs font-medium text-white">
          <i className="ri-fire-fill"></i>
          本周推荐 · {active.type === "movie" ? "电影" : "电视剧"}
        </span>
        <h1 className="max-w-4xl font-heading text-3xl font-extrabold leading-tight text-white md:text-5xl">
          {active.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-white/80">
          <span className="flex items-center gap-1 text-accent-400">
            <i className="ri-star-fill"></i>
            {active.rating.toFixed(1)}
          </span>
          <span>{active.year}</span>
          <span>{active.region}</span>
          <span>{active.genres.join(" · ")}</span>
        </div>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-white/75 md:text-base">
          {active.summary}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to={`/detail/${active.id}`}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
          >
            <i className="ri-play-fill text-lg"></i>
            立即观看
          </Link>
          <Link
            to={`/detail/${active.id}`}
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/20"
          >
            <i className="ri-information-line text-lg"></i>
            作品详情
          </Link>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/30 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/30 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-white/50 hover:bg-white/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}